import { Calendar, CheckCircle2, Video, Clock, Sparkles, Target, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import BookingCalendar from "./BookingCalendar";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import whatsappIcon from "@/assets/whatsapp-icon.png";
const BookSession = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const programs = [{
    id: "discovery",
    icon: Video,
    title: "Discovery Call",
    duration: "20 minutes",
    price: "FREE",
    isFree: true,
    description: "Perfect for understanding our approach and finding the right program for you",
    features: ["Understand how functional nutrition works", "Discuss your health concerns", "Learn about our program structure", "Determine the most appropriate program"],
    cta: "Book Free Discovery Call",
    action: "book"
  }, {
    id: "consult",
    icon: Calendar,
    title: "Initial Consultation",
    duration: "30-45 minutes",
    description: "Comprehensive assessment with lab work review and symptom analysis",
    features: ["Detailed health history review", "Lab work analysis & interpretation", "Discussion of symptoms & health goals", "Initial nutrition recommendations", "Supplement guidance if needed"],
    cta: "Contact Us",
    action: "whatsapp"
  }, {
    id: "programs",
    icon: Target,
    title: "Personalized Programs",
    duration: "3 or 6 months",
    featured: true,
    description: "Comprehensive root cause approach for lasting transformation",
    features: ["Root cause analysis of health concerns", "Weekly personalized diet plans", "Targeted supplement protocols", "Strategies for mental wellbeing", "Regular progress monitoring", "Ongoing support & adjustments"],
    cta: "Contact Us",
    action: "whatsapp"
  }];
  const handleBookDiscovery = () => {
    setSelectedProgram("discovery");
    setShowBooking(true);
    // Scroll to booking calendar section
    setTimeout(() => {
      document.getElementById('booking-calendar-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };
  const handleWhatsAppContact = () => {
    window.open("https://wa.me/919910961228?text=Hi!%20I'm%20interested%20in%20learning%20more%20about%20your%20consultation%20programs.", "_blank");
  };
  return <section id="book-session" className="section-padding container-custom bg-gradient-to-b from-secondary/20 to-background">
      <div className="space-y-8 sm:space-y-12">
        <div className="text-center space-y-3 sm:space-y-4 max-w-3xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground">
            The Consultations We Provide
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Choose the right path for your wellness journey. Start with a free discovery call to explore how we can help you achieve your health goals.
          </p>
        </div>

        {!showBooking ? <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 px-4">
              {programs.map((program, index) => <Card key={index} className={`card-enhanced p-6 sm:p-8 flex flex-col hover:shadow-xl transition-all duration-300 border-border relative overflow-hidden ${program.featured ? 'ring-2 ring-primary/30' : ''}`}>
                  {program.featured && <div className="absolute top-0 right-0">
                      <Badge className="bg-primary text-primary-foreground rounded-bl-lg rounded-tr-lg px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>}

                  {program.isFree && <div className="absolute top-0 right-0">
                      <Badge className="bg-accent text-accent-foreground rounded-bl-lg rounded-tr-lg px-4 py-1">
                        FREE
                      </Badge>
                    </div>}

                  {/* Header Section - Fixed Height for Alignment */}
                  <div className="space-y-4 flex-shrink-0">
                    

                    <div className="space-y-3">
                      <h3 className="text-xl sm:text-2xl font-bold text-card-foreground min-h-[4rem] flex items-center">
                        {program.title}
                      </h3>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-medium">{program.duration}</span>
                      </div>
                      {program.price}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm leading-relaxed flex-shrink-0">
                    {program.description}
                  </p>

                  {/* Features List - Grows to fill space */}
                  <div className="space-y-3 flex-grow">
                    <div className="organic-separator"></div>
                    <ul className="space-y-3 mt-3">
                      {program.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button - Fixed at bottom */}
                  <Button size="lg" onClick={program.action === "book" ? handleBookDiscovery : handleWhatsAppContact} className={`w-full min-h-[52px] text-base font-semibold shadow-sm flex-shrink-0 mt-6 ${program.isFree ? "bg-accent hover:bg-accent/90 text-accent-foreground shadow-accent/20" : program.featured ? "bg-primary hover:bg-primary/90 text-primary-foreground shadow-primary/20" : "bg-secondary hover:bg-secondary/90 text-secondary-foreground"}`}>
                    {program.action === "whatsapp"}
                    {program.cta}
                  </Button>
                </Card>)}
            </div>

            <div className="text-center space-y-4 mt-8 px-4">
              <div className="bg-secondary/10 rounded-2xl p-8 border border-border shadow-sm max-w-2xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <h3 className="text-xl font-semibold text-foreground">
                    Need Help Deciding?
                  </h3>
                </div>

                <p className="text-muted-foreground mb-6 text-base leading-relaxed">
                  Not sure which program is right for you? Book a free discovery call to discuss your needs.
                </p>

              <Button size="lg" onClick={handleBookDiscovery} className="bg-accent hover:bg-accent/90 text-accent-foreground w-full px-4 sm:px-6 py-4 sm:py-5 text-sm sm:text-base rounded-full shadow-sm transition-all duration-200 hover:shadow-md">
                Book Free Discovery Call Now
              </Button>
              </div>



              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4" />
                Questions? Contact us:
                <a href="https://wa.me/919910961228" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                  +91 9910961228
                </a>
              </p>
            </div>
          </> : <div id="booking-calendar-container" className="space-y-4">
            <Button variant="ghost" onClick={() => {
          setShowBooking(false);
          setSelectedProgram(null);
        }} className="mb-2">
              ← Back to Programs
            </Button>
            <div className="text-center mb-4">
              <Badge className="bg-accent text-accent-foreground mb-2">Discovery Call - FREE</Badge>
              <p className="text-muted-foreground text-sm">Select a time that works for you</p>
            </div>
            <BookingCalendar />
          </div>}
      </div>
    </section>;
};
export default BookSession;